import type { ReactNode } from 'react';
import { Fragment } from 'react';
import {
  Badge,
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Progress,
} from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import {
  getExportGroup,
  getMenuGroupsForStatus,
  getCopyMenuGroups,
  type ProgramAction,
  type ProgramBaseType,
  type ProgramStatus,
} from '../program-actions-menu';
import {
  ArchiveIcon,
  BanIcon,
  ChevronDownIcon,
  FilePenIcon,
  FlagIcon,
  MoreVerticalIcon,
  RadioIcon,
} from './icons';

// ProgramStatus is imported from the shared actions menu and re-exported for consumers.
export type { ProgramStatus };

export type ProgramEditStep = 'info' | 'benefits' | 'faqs';

export interface ProgramEditShellProps {
  status: ProgramStatus;
  /** Company logo node (workspace) — replaces the generic archive glyph next to
   *  the "Edição de programa" title. */
  brand?: ReactNode;
  subtitle: string;
  progress: number;
  currentStep: number;
  totalSteps: number;
  activeStep: ProgramEditStep;
  isStepsOpen: boolean;
  onStepsOpenChange: (open: boolean) => void;
  onStepChange: (step: ProgramEditStep) => void;
  isSubmitting: boolean;
  isDirty: boolean;
  /**
   * EU-570 — modo somente visualização (programa Encerrado / Cancelado).
   * Desabilita todo o conteúdo do form (fieldset nativo), troca o rodapé:
   * etapas intermediárias mantêm "Avançar" (só navegação), a última mostra
   * "Salvar alterações" desabilitado. "Voltar" e o sumário "Conferir etapas"
   * permanecem habilitados.
   */
  readOnly?: boolean;
  isCopy?: boolean;
  onDiscardCopy?: () => void;
  onBack: () => void;
  onSaveChanges: () => void;
  onSubmit: () => void;
  /** Called when the user picks an action from the header ellipsis menu. */
  onHeaderAction?: (action: ProgramAction) => void;
  /**
   * EU-654 RN-02/03 — tipos de base ofertáveis na seção "Extrair bases" do
   * menu do header (vêm do backend); `undefined` = disponibilidade carregando.
   */
  exportTypes?: ProgramBaseType[];
  children: ReactNode;
  /**
   * QA EU-641 — conteúdo renderizado FORA do <fieldset disabled> do EU-570
   * (mas dentro do form). Para elementos interativos de LEITURA que precisam
   * seguir clicáveis em programa Encerrado/Cancelado — ex.: o card "FAQs
   * padrão" com o link "Conferir perguntas" (espelha a estrutura da vaga,
   * onde o card fica fora do fieldset).
   */
  afterFieldset?: ReactNode;
}

const STATUS_LABEL: Record<ProgramStatus, string> = {
  ativa: 'Ativa',
  'ativo-privado': 'Ativo privado',
  rascunho: 'Rascunho',
  encerrado: 'Encerrada',
  cancelado: 'Cancelado',
};

/**
 * Status-aware header badge. Mirrors the listing's StatusBadge so the edit /
 * read-only header shows each status with its own color + icon. Previously
 * only `rascunho` was special-cased and every other status (including the
 * terminal `cancelado` / `encerrado`) fell into the generic green "active"
 * badge — so a cancelled program looked identical to an active one.
 */
function StatusBadge({ status }: { status: ProgramStatus }) {
  if (status === 'rascunho') {
    return (
      <Badge className="self-start bg-warning text-primary-foreground">
        <FilePenIcon className="size-3" />
        {STATUS_LABEL.rascunho}
      </Badge>
    );
  }
  if (status === 'encerrado') {
    return (
      <Badge className="self-start bg-primary text-primary-foreground">
        <FlagIcon className="size-3" />
        {STATUS_LABEL.encerrado}
      </Badge>
    );
  }
  if (status === 'cancelado') {
    return (
      <Badge className="self-start bg-destructive text-primary-foreground">
        <BanIcon className="size-3" />
        {STATUS_LABEL.cancelado}
      </Badge>
    );
  }
  // ativa / ativo-privado — the green "active" badge.
  return (
    <Badge className="self-start bg-secondary text-primary">
      <RadioIcon className="size-3" />
      {STATUS_LABEL[status]}
    </Badge>
  );
}

const STEP_OPTIONS: readonly { value: ProgramEditStep; label: string }[] = [
  { value: 'info', label: 'Informações gerais' },
  { value: 'benefits', label: 'Benefícios' },
  { value: 'faqs', label: 'Perguntas frequentes' },
];

export function ProgramEditShell({
  status,
  brand,
  subtitle,
  progress,
  currentStep,
  totalSteps,
  activeStep,
  isStepsOpen,
  onStepsOpenChange,
  onStepChange,
  isSubmitting,
  isDirty,
  readOnly = false,
  isCopy = false,
  onDiscardCopy,
  onBack,
  onSaveChanges,
  onSubmit,
  onHeaderAction,
  exportTypes,
  children,
  afterFieldset,
}: ProgramEditShellProps) {
  const effectiveStatus: ProgramStatus = isCopy ? 'rascunho' : status;
  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="programs" />

      <main className="flex flex-1 flex-col items-center gap-8 bg-card px-4 pt-6 pb-16 md:px-8 md:pt-8">
        <div className="flex w-full max-w-[630px] flex-col gap-8">
          <div className="flex flex-col gap-5">
            <StatusBadge status={effectiveStatus} />
            <div className="flex flex-col gap-2">
              <div className="flex flex-row items-center gap-2">
                {brand ?? (
                  <div className="flex size-8 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
                    <ArchiveIcon className="size-4" />
                  </div>
                )}
                <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
                  Edição de programa
                </h1>
              </div>
              <div className="flex flex-row items-center justify-between gap-2">
                <p className="text-xl font-medium leading-none tracking-tight text-accent-foreground md:text-2xl">
                  {subtitle}
                </p>
                {onHeaderAction && (
                  <HeaderActionsMenu
                    status={effectiveStatus}
                    isCopy={isCopy}
                    onAction={onHeaderAction}
                    exportTypes={exportTypes}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <Progress value={progress} className="h-2 bg-secondary/40" />
            <div className="flex flex-row items-center justify-between">
              <span className="text-sm font-medium leading-none text-muted-foreground">
                Etapa {currentStep}/{totalSteps}
              </span>
              <DropdownMenu open={isStepsOpen} onOpenChange={onStepsOpenChange}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex flex-row items-center gap-0.5 text-sm font-medium leading-none text-primary hover:underline focus-visible:outline-none"
                  >
                    {isStepsOpen ? 'Ocultar etapas' : 'Conferir etapas'}
                    <ChevronDownIcon
                      className={cn('size-4 transition-transform', isStepsOpen && 'rotate-180')}
                    />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={8} className="w-[344px] p-1.5">
                  {STEP_OPTIONS.map((option) => {
                    const isActive = option.value === activeStep;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => onStepChange(option.value)}
                        className={cn(
                          'flex w-full flex-row items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm leading-5 text-foreground outline-none transition-colors',
                          isActive ? 'bg-secondary' : 'bg-card hover:bg-accent'
                        )}
                      >
                        <span className="flex-1">{option.label}</span>
                      </button>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <form
            className="flex flex-col gap-5"
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
            noValidate
          >
            {/* EU-570 — fieldset nativo desabilita TODO controle de form
                interno (inputs, radios, checkboxes, botões dos cards) de uma
                vez; RichTextEditor/DateTimePicker recebem `disabled` explícito
                nas páginas (contentEditable não obedece fieldset). */}
            <fieldset
              disabled={readOnly}
              className="contents"
              data-testid={readOnly ? 'program-edit-readonly-fieldset' : undefined}
            >
              {children}
            </fieldset>

            {/* QA EU-641 — fora do fieldset: segue interativo em read-only. */}
            {afterFieldset}

            {readOnly && !isCopy ? (
              <div className="flex flex-row items-center justify-between gap-3 pt-3">
                <Button type="button" variant="outline" size="lg" onClick={onBack}>
                  Voltar
                </Button>
                {currentStep === totalSteps ? (
                  <Button type="button" size="lg" disabled data-testid="program-edit-save-blocked">
                    Salvar alterações
                  </Button>
                ) : (
                  <Button type="submit" size="lg" data-testid="program-edit-readonly-next">
                    Avançar
                  </Button>
                )}
              </div>
            ) : isCopy ? (
              <div className="flex flex-row items-center justify-end gap-2 pt-3">
                <Button
                  type="button"
                  size="lg"
                  onClick={onDiscardCopy}
                  className="bg-secondary text-primary shadow-2xs hover:bg-secondary/80"
                >
                  Descartar cópia
                </Button>
                <Button type="submit" size="lg" loading={isSubmitting}>
                  Avançar
                </Button>
              </div>
            ) : (
              <div className="flex flex-row items-center justify-between gap-3 pt-3">
                <Button type="button" variant="outline" size="lg" onClick={onBack}>
                  Voltar
                </Button>
                <div className="flex flex-row items-center gap-2">
                  {isDirty && (
                    <Button
                      type="button"
                      size="lg"
                      onClick={onSaveChanges}
                      loading={isSubmitting}
                      className="bg-secondary text-primary shadow-2xs hover:bg-secondary/80"
                    >
                      Salvar alterações
                    </Button>
                  )}
                  <Button type="submit" size="lg" loading={isSubmitting}>
                    Avançar
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header 3-dot menu (Figma 4, 5, 7, 2)
// ---------------------------------------------------------------------------

interface HeaderActionsMenuProps {
  status: ProgramStatus;
  isCopy: boolean;
  onAction: (action: ProgramAction) => void;
  /** EU-654 - tipos de base ofertaveis; undefined = carregando. */
  exportTypes: ProgramBaseType[] | undefined;
}

function HeaderActionsMenu({ status, isCopy, onAction, exportTypes }: HeaderActionsMenuProps) {
  const groups = isCopy ? getCopyMenuGroups() : getMenuGroupsForStatus(status);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Ações do programa"
          className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-border/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 data-[state=open]:bg-border/50 data-[state=open]:text-foreground"
        >
          <MoreVerticalIcon className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        {/* Épico EU-561 — mesma seção "Extrair bases" da listagem. Não aparece
            na edição de uma CÓPIA: o rascunho duplicado ainda não tem
            inscritos, então não há base a extrair. EU-654 — lista DINÂMICA
            (RN-02/03): só o que o backend ofertou; undefined = carregando. */}
        {!isCopy && (
          <>
            <DropdownMenuLabel>Extrair bases</DropdownMenuLabel>
            <DropdownMenuGroup>
              {exportTypes === undefined ? (
                <DropdownMenuItem disabled data-testid="program-edit-export-loading">
                  Carregando tipos…
                </DropdownMenuItem>
              ) : (
                getExportGroup(exportTypes).map((option) => (
                  <DropdownMenuItem key={option.action} onClick={() => onAction(option.action)}>
                    {option.label}
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Gerenciar programa</DropdownMenuLabel>
          </>
        )}
        {groups.map((group, groupIndex) => (
          <Fragment key={groupIndex}>
            {groupIndex > 0 && <DropdownMenuSeparator />}
            <DropdownMenuGroup>
              {group.map((option) => (
                <DropdownMenuItem
                  key={option.action}
                  destructive={option.destructive}
                  onClick={() => onAction(option.action)}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
